import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  projectApi,
  chatApi,
} from "../api/client";
import type { Project, ProjectFile, Chat } from "../types";
import "./ProjectDetail.css";
import { useToast } from "../lib/toast";

export default function ProjectDetailView() {
  const toast = useToast();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [chats, setChats] = useState<Chat[]>([]);
  const [tab, setTab] = useState<"chats" | "files" | "instructions">("chats");
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [fileContent, setFileContent] = useState<{ filename: string; content: string; file_type: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!projectId) return;
    try {
      const p = await projectApi.get(projectId);
      setProject(p);
      setName(p.name);
      setDescription(p.description);
      setInstructions(p.instructions);
      setFiles(await projectApi.files(projectId));
      setChats(await chatApi.list(projectId));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const save = async () => {
    if (!projectId) return;
    await projectApi.update(projectId, { name, description, instructions });
    setEditing(false);
    load();
  };

  const remove = async () => {
    if (!projectId || !confirm("Delete this project?")) return;
    await projectApi.remove(projectId);
    navigate("/projects");
  };

  const toggleWikiInContext = async () => {
    if (!projectId || !project) return;
    const next = !project.include_wiki_in_context;
    setProject({ ...project, include_wiki_in_context: next });
    try {
      await projectApi.update(projectId, { include_wiki_in_context: next });
    } catch (e) {
      setProject({ ...project, include_wiki_in_context: !next });
      toast.error((e as Error).message);
    }
  };

  const newChat = async () => {
    if (!projectId) return;
    const c = await chatApi.create({ project_id: projectId, title: "New Chat" });
    navigate(`/chat/${c.id}`);
  };

  const upload = async (f: File) => {
    if (!projectId) return;
    try {
      await projectApi.uploadFile(projectId, f);
      setFiles(await projectApi.files(projectId));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const viewFile = async (f: ProjectFile) => {
    if (!projectId) return;
    try {
      const c = await projectApi.fileContent(projectId, f.id);
      setFileContent({ filename: c.filename, content: c.content, file_type: c.file_type });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const deleteFile = async (f: ProjectFile) => {
    if (!projectId || !confirm(`Delete ${f.filename}?`)) return;
    await projectApi.removeFile(projectId, f.id);
    setFiles(await projectApi.files(projectId));
  };

  if (!project) return <div className="view"><div className="empty-state"><span className="spinner" /></div></div>;

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">{project.name}</h2>
          <p className="view-subtitle">{project.description || "No description"}</p>
        </div>
        <div className="row">
          <Link className="btn btn-sm" to={`/projects/${project.id}/wiki`}>
            📖 Wiki
          </Link>
          <button className="btn btn-sm" onClick={() => setEditing((e) => !e)}>
            {editing ? "Cancel" : "Edit"}
          </button>
          <button className="btn btn-danger btn-sm" onClick={remove}>
            Delete
          </button>
        </div>
      </div>

      {editing && (
        <div className="card fade-in" style={{ marginBottom: "var(--gap-lg)" }}>
          <div className="col">
            <div>
              <label className="form-label">Name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea className="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Instructions</label>
              <textarea className="textarea" style={{ minHeight: 160 }} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
            </div>
            <button className="btn btn-primary" onClick={save}>Save</button>
          </div>
        </div>
      )}

      <div className="tabs">
        <button className={tab === "chats" ? "tab active" : "tab"} onClick={() => setTab("chats")}>Chats</button>
        <button className={tab === "files" ? "tab active" : "tab"} onClick={() => setTab("files")}>Files</button>
        <button className={tab === "instructions" ? "tab active" : "tab"} onClick={() => setTab("instructions")}>Instructions</button>
      </div>

      {tab === "chats" && (
        <div className="col">
          <button className="btn btn-primary" style={{ alignSelf: "flex-start" }} onClick={newChat}>
            + New Chat in Project
          </button>
          {chats.length === 0 && <div className="empty-state"><h3>No chats</h3></div>}
          {chats.map((c) => (
            <Link key={c.id} to={`/chat/${c.id}`} className="card project-chat-row">
              <span className="sidebar-chat-dot" />
              <span style={{ flex: 1 }}>{c.title}</span>
              <span className="dim" style={{ fontSize: 11 }}>{c.model_override || ""}</span>
            </Link>
          ))}
        </div>
      )}

      {tab === "files" && (
        <div className="col">
          <div
            className="file-drop"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              Array.from(e.dataTransfer.files).forEach(upload);
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => Array.from(e.target.files || []).forEach(upload)}
            />
            <button className="btn" onClick={() => fileInputRef.current?.click()}>
              📎 Upload Files
            </button>
            <span className="muted" style={{ marginLeft: 12 }}>or drag and drop here</span>
          </div>
          {files.length === 0 && <div className="empty-state"><h3>No files</h3></div>}
          {files.map((f) => (
            <div key={f.id} className="card file-row">
              <span className="file-icon">📄</span>
              <span style={{ flex: 1 }}>{f.filename}</span>
              <span className="dim" style={{ fontSize: 11 }}>
                {(f.file_size / 1024).toFixed(1)} KB
              </span>
              <button className="btn btn-sm" onClick={() => viewFile(f)}>View</button>
              <button className="btn btn-danger btn-sm" onClick={() => deleteFile(f)}>Delete</button>
            </div>
          ))}
          {fileContent && (
            <div className="card file-viewer">
              <div className="row-between" style={{ marginBottom: 12 }}>
                <strong>{fileContent.filename}</strong>
                <span className="muted" style={{ fontSize: "0.85em" }}>{fileContent.file_type}</span>
                <button className="btn btn-sm btn-ghost" onClick={() => setFileContent(null)}>×</button>
              </div>
              <pre className="file-pre">{fileContent.content}</pre>
            </div>
          )}
        </div>
      )}

      {tab === "instructions" && (
        <div className="col">
          <label className="settings-toggle-row card">
            <div>
              <div className="settings-toggle-name">Include wiki in chat context</div>
              <div className="dim" style={{ fontSize: 12 }}>
                When on, project chats also see a "## Project Wiki" section (page titles + content)
                alongside project files, sharing the same context budget.
              </div>
            </div>
            <input
              type="checkbox"
              checked={project.include_wiki_in_context}
              onChange={toggleWikiInContext}
            />
          </label>
          <div className="card">
            {project.instructions ? (
              <pre className="file-pre" style={{ whiteSpace: "pre-wrap" }}>{project.instructions}</pre>
            ) : (
              <div className="empty-state"><h3>No instructions</h3><p>Click Edit to add custom instructions.</p></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}